import React, { ChangeEvent } from 'react';
import { Checkbox, ControlLabel, FormControl, FormGroup, HelpBlock } from 'react-bootstrap';
import Select from 'react-select';
import { TranslatedLabels } from '../../shared/types';
import { Translate } from '../../shared/LanguageService';

export type SelectOption = {
    value: number;
    label: string;
};

export type PlanGenerationTemplate = {
    id: number;
    name: string;
};

type PlanTemplateFormErrors = {
    name?: string;
};

type ExternalProps = {
    planTemplateName: string;
    onChangePlanTemplateName: (newValue: string) => void;
    availablePlanGenerationTemplates?: PlanGenerationTemplate[];
    planGenerationTemplate: SelectOption | null;
    onChangePlanGenerationTemplate: (newValue: SelectOption) => void;
    errors: PlanTemplateFormErrors | null;
    isAutoGenerationActive: boolean;
    onChangeIsAutoGenerationActive: (newValue: boolean) => void;
    onImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
    imageReferences: string[];
    imageModal: (imageReference: string) => void;
};

type PlanTemplateFormProps = {
    labels: TranslatedLabels;
} & ExternalProps;

function transformToSelectOption(planGenerationTemplate: PlanGenerationTemplate): SelectOption {
    return {
        value: planGenerationTemplate.id,
        label: planGenerationTemplate.name
    };
}

export function sortPlanGenerationTemplatesAscending(
    planGenerationTemplateA: SelectOption,
    planGenerationTemplateB: SelectOption
): -1 | 0 | 1 {
    if (planGenerationTemplateA.label > planGenerationTemplateB.label) {
        return 1;
    }

    if (planGenerationTemplateA.label < planGenerationTemplateB.label) {
        return -1;
    }

    return 0;
}

export function PlanTemplateForm({
    labels,
    planTemplateName,
    onChangePlanTemplateName,
    availablePlanGenerationTemplates,
    planGenerationTemplate,
    onChangePlanGenerationTemplate,
    errors,
    isAutoGenerationActive,
    onChangeIsAutoGenerationActive,
    onImageChange,
    imageReferences,
    imageModal
}: PlanTemplateFormProps) {
    function handlePlanTemplateNameChange(event: ChangeEvent<HTMLInputElement>) {
        onChangePlanTemplateName(event.target.value);
    }

    function handlePlanGenerationTemplateChange(selectedOption: SelectOption) {
        onChangePlanGenerationTemplate(selectedOption);
    }

    function handleIsAutoGenerationActiveChange(event: ChangeEvent<HTMLInputElement>) {
        event.persist();
        onChangeIsAutoGenerationActive(event.target.checked);
    }

    const { apiUrl } = window.appConfig;

    return (
        <>
            <form>
                <FormGroup controlId="name">
                    <ControlLabel>{labels.planTemplateName}</ControlLabel>
                    <FormControl
                        autoFocus
                        type="text"
                        name="name"
                        value={planTemplateName}
                        onChange={handlePlanTemplateNameChange}
                    />
                    {errors && errors.name ? <HelpBlock>{errors.name}</HelpBlock> : null}
                </FormGroup>
                <div>
                    <label htmlFor="plan-generation-template-select" id="plan-generation-template">
                        {labels.planGenerationTemplate}
                    </label>
                    <Select
                        id="plan-generation-template-select"
                        aria-labelledby="plan-generation-template"
                        className="planTemplate__dropdown"
                        multi={false}
                        clearable={true}
                        placeholder={labels.planGenerationTemplatePlaceholder}
                        noResultsText={labels.planGenerationTemplateNoResultsText}
                        options={
                            availablePlanGenerationTemplates
                                ? availablePlanGenerationTemplates
                                      .map(transformToSelectOption)
                                      .sort(sortPlanGenerationTemplatesAscending)
                                : []
                        }
                        value={planGenerationTemplate}
                        onChange={handlePlanGenerationTemplateChange}
                    />
                </div>
                {planGenerationTemplate ? (
                    <FormGroup>
                        <Checkbox
                            name="isAutoGenerationActive"
                            checked={isAutoGenerationActive}
                            onChange={handleIsAutoGenerationActiveChange}
                        >
                            <span>{labels.autoGenerationActive}</span>
                        </Checkbox>
                    </FormGroup>
                ) : null}
                <FormGroup controlId="file">
                    <ControlLabel>{labels.filesUpload}</ControlLabel>
                    <FormControl
                        type="file"
                        name="file"
                        accept="image/png, image/jpeg, image/jpg"
                        onChange={onImageChange}
                        multiple
                    />
                </FormGroup>
            </form>
            {imageReferences.map((imageReference) => (
                <img
                    src={`${apiUrl}/file/${imageReference}`}
                    onClick={() => imageModal(imageReference)}
                    style={{ maxWidth: '100px', maxHeight: '100px' }}
                    key={imageReference}
                />
            ))}
        </>
    );
}

function TranslatedPlanTemplateForm(props: ExternalProps) {
    const labels: TranslatedLabels = {
        planTemplateName: Translate('plan-templates/modal/form/input-label/name'),
        planGenerationTemplate: Translate(
            'plan-templates/modal/form/input-label/plan-generation-template'
        ),
        planGenerationTemplatePlaceholder: Translate(
            'plan-templates/modal/form/input-label/plan-generation-template/placeholder'
        ),
        planGenerationTemplateNoResultsText: Translate(
            'plan-templates/modal/form/input-label/plan-generation-template/no-result'
        ),
        autoGenerationActive: Translate(
            'plan-templates/modal/form/input-label/is-auto-generation-active'
        ),
        filesUpload: Translate('plan-templates/modal/form/input-label/files')
    };

    return <PlanTemplateForm {...props} labels={labels} />;
}

export default TranslatedPlanTemplateForm;
